import React, { useEffect, useState } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { autoAttributesPlugin, growthbookTrackingPlugin } from '@growthbook/growthbook/plugins';

import PropTypes from 'prop-types';

// CRA bakes REACT_APP_* env vars in at build time. Without an apiHost +
// clientKey the SDK still evaluates locally against in-code defaults, so
// feature consumers keep working even when remote config is absent.
const isGrowthBookRemoteEnabled = Boolean(
  process.env.REACT_APP_GROWTHBOOK_API_HOST
  && process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
);

// Flipped true once gb.init() has loaded remote feature definitions. Used by the
// tracking plugin's eventFilter to tell apart the two kinds of "unknownFeature"
// evaluations: transient pre-load ones (noise, dropped) vs. post-load ones, which
// mean a real misconfig (typo'd key, deleted flag, wrong env) and must be kept.
let gbReady = false;

// We initialize the GrowthBook client outside of the provider component so the
// same instance is reused across the app and not re-created on every render.
// Remote config loads via gb.init() in the provider's useEffect, but the same
// instance can still serve local-only features/attributes before that resolves.
export const createGrowthBook = () => {
  try {
    return new GrowthBook({
      apiHost: process.env.REACT_APP_GROWTHBOOK_API_HOST,
      clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
      decryptionKey: process.env.REACT_APP_GROWTHBOOK_DECRYPTION_KEY,
      enableDevMode: process.env.REACT_APP_TARGET_ENV !== 'prod',
      plugins: isGrowthBookRemoteEnabled
        ? [
          autoAttributesPlugin(),
          growthbookTrackingPlugin({
            // Drop only the transient pre-load "Feature Evaluated" events that
            // fire before gb.init() loads definitions. Post-load unknownFeature
            // events are kept — those flag real misconfigs worth surfacing.
            eventFilter: ({ eventName, properties }) => !(
              eventName === 'Feature Evaluated'
              && properties?.source === 'unknownFeature'
              && !gbReady
            ),
          }),
        ]
        : [autoAttributesPlugin()],
      // we can turn these on to forward to GA,Sentry,Datadog and link feature flags
      // trackingCallback: datadogTrackingCallback,
      // onFeatureUsage: (featureKey, result) => {
      //   trackFeatureFlagEvaluation(featureKey, result.value);
      // },
    });
  } catch (error) {
    // Last-resort fallback: a bare client so feature lookups return defaults
    // instead of crashing whatever code path triggered construction.
    // eslint-disable-next-line no-console
    console.error('[growthbook] client construction failed; falling back to defaults', error);
    return new GrowthBook();
  }
};

/**
 * @param children
 * @returns {JSX.Element}
 *
 * Initializes the GrowthBook client and exposes feature flags / experiments
 * to the tree via @growthbook/growthbook-react hooks (useFeatureIsOn, etc).
 */
export const GrowthbookAppProvider = ({ children }) => {
  const [gb] = useState(createGrowthBook);

  useEffect(() => {
    if (isGrowthBookRemoteEnabled) {
      gb.init({ streaming: true, timeout: 2000 })
        .then(() => { gbReady = true; })
        // eslint-disable-next-line no-console
        .catch(error => console.error('[growthbook] init failed', error));
    }
  }, [gb]);

  return (
    <GrowthBookProvider growthbook={gb}>
      {children}
    </GrowthBookProvider>
  );
};

GrowthbookAppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
